import { Concern, Room, chooseTarget, requiredCfm, achievedEach, SizingTarget, Cfm } from './calculator';
import {
  MODELS, getDecision, getTests, getOffer, getFilterSet, PerformanceTest, ModelVariant, PerfMetric,
} from '@/data/catalogue';

export type Country = 'US' | 'other';
export type IonizerPref = 'any' | 'off-or-none' | 'none';
export type SortKey = 'three-year' | 'purchase' | 'output' | 'noise';
export type GasSource = 'cooking' | 'renovation' | 'smoke-odour' | 'pets' | 'musty' | 'unknown';
export type GasPersistence = 'occasional' | 'ongoing';

export interface CostAssumptions {
  hoursPerDay: number;
  tariffUsdPerKwh: number;
  /** Multiplies every filter replacement interval (0.5 = replace twice as often) */
  filterIntervalFactor: number;
}
export const FILTER_FACTORS = [0.5, 0.75, 1, 1.5] as const;
export const DEFAULT_COSTS: CostAssumptions = { hoursPerDay: 12, tariffUsdPerKwh: 0.17, filterIntervalFactor: 1 };

export interface Preferences {
  country: Country;
  concerns: Concern[];
  customEach: number | null;
  maxNoiseDbA: number | null;
  maxPurchaseUsd: number | null;
  maxAnnualUsd: number | null;
  allowTwoUnits: boolean;
  diyOnly: boolean;
  ionizer: IonizerPref;
  costs: CostAssumptions;
  sort: SortKey;
  gasSource: GasSource | null;
  gasPersistence: GasPersistence | null;
}
export const DEFAULT_PREFS: Preferences = {
  country: 'US', concerns: ['general'], customEach: null, maxNoiseDbA: null, maxPurchaseUsd: null,
  maxAnnualUsd: null, allowTwoUnits: true, diyOnly: false, ionizer: 'any', costs: DEFAULT_COSTS, sort: 'three-year',
  gasSource: null, gasPersistence: null,
};

export const PREF_LIMITS = {
  noise: { min: 15, max: 80 },
  budget: { min: 1, max: 100000 },
  hours: { min: 0.5, max: 24 },
  tariff: { min: 0.01, max: 2 },
  each: { min: 1, max: 20 },
} as const;

export type PrefErrors = Partial<Record<'noise' | 'purchase' | 'annual' | 'hours' | 'tariff' | 'factor' | 'each', string>>;
const inRange = (v: number, r: { min: number; max: number }) => Number.isFinite(v) && v >= r.min && v <= r.max;

export function validatePrefs(p: Preferences): PrefErrors {
  const e: PrefErrors = {};
  if (p.maxNoiseDbA != null && !inRange(p.maxNoiseDbA, PREF_LIMITS.noise)) e.noise = `Noise limit must be ${PREF_LIMITS.noise.min}–${PREF_LIMITS.noise.max} dBA.`;
  if (p.maxPurchaseUsd != null && !inRange(p.maxPurchaseUsd, PREF_LIMITS.budget)) e.purchase = `Purchase budget must be $${PREF_LIMITS.budget.min}–$${PREF_LIMITS.budget.max.toLocaleString()}.`;
  if (p.maxAnnualUsd != null && !inRange(p.maxAnnualUsd, PREF_LIMITS.budget)) e.annual = `Yearly budget must be $${PREF_LIMITS.budget.min}–$${PREF_LIMITS.budget.max.toLocaleString()}.`;
  if (!inRange(p.costs.hoursPerDay, PREF_LIMITS.hours)) e.hours = `Hours per day must be ${PREF_LIMITS.hours.min}–${PREF_LIMITS.hours.max}.`;
  if (!inRange(p.costs.tariffUsdPerKwh, PREF_LIMITS.tariff)) e.tariff = `Electricity price must be $${PREF_LIMITS.tariff.min}–$${PREF_LIMITS.tariff.max} per kWh.`;
  if (!(FILTER_FACTORS as readonly number[]).includes(p.costs.filterIntervalFactor)) e.factor = 'Choose a filter-life option.';
  if (p.customEach != null && !inRange(p.customEach, PREF_LIMITS.each)) e.each = `Air changes per hour must be ${PREF_LIMITS.each.min}–${PREF_LIMITS.each.max}.`;
  return e;
}

export interface Costs {
  purchase: number | null;
  annualElectricity: number | null;
  /** Never "measured" unless power was measured at the matched speed */
  electricityBasis: 'measured-at-speed' | 'rated-max-power-upper-estimate' | null;
  /** Long-run yearly filter cost (price × 12 ÷ interval) */
  annualFilterMaintenance: number | null;
  /** Actual filter boxes bought in the first 36 months, after the included filters run out */
  filterBoxes36: number | null;
  filterSpend36: number | null;
  threeYear: number | null;
  missing: string[];
}

export interface Setup {
  model: ModelVariant;
  units: number;
  /** Test(s) used — one per required metric, all at the same speed */
  tests: PerformanceTest[];
  metricsUsed: PerfMetric[];
  /** Per-unit limiting output (lowest of required metrics) */
  perUnitCfm: number;
  totalCfm: number;
  achievedEach: number;
  /** Combined sound for the setup at that speed, if measured */
  combinedNoiseDbA: number | null;
  costs: Costs;
  caveats: string[];
}

export interface Unresolved { model: ModelVariant; reasons: string[] }

export interface MatchResult {
  target: SizingTarget | null;
  required: Cfm | null;
  requiredMetrics: PerfMetric[];
  qualifying: Setup[];
  unknown: Unresolved[];
  excluded: Unresolved[];
  notes: string[];
}

/**
 * Each selected particle concern requires its own metric. No silent substitution.
 *  wildfire → smoke; general → smoke; pets / dust → dust; allergies → pollen.
 * A CADR with no stated particle type may stand in only when the sole concern is
 * "general", and is always labelled.
 */
export function requiredMetrics(concerns: Concern[]): PerfMetric[] {
  const m = new Set<PerfMetric>();
  for (const c of concerns) {
    if (c === 'wildfire' || c === 'general') m.add('smoke');
    if (c === 'pets' || c === 'dust') m.add('dust');
    if (c === 'allergies') m.add('pollen');
  }
  return [...m];
}

/** Normalized identifier: lowercase letters and digits only. */
const normId = (x: string) => x.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Explicit identifiers a filter's compatibility list may use for each model. Exact matches only. */
export const MODEL_ALIASES: Record<string, string[]> = {
  'levoit-core-300s-p': ['Core 300S-P'],
  'levoit-core-400s-p': ['Core 400S-P'],
  'levoit-core-600s-p': ['Core 600S-P'],
  'levoit-vital-100s-p': ['Vital 100S-P'],
  'levoit-vital-200s-p': ['Vital 200S-P'],
  'coway-ap-1512hh': ['AP-1512HH'],
  'coway-airmega-150': ['Airmega 150'],
  'coway-airmega-250': ['Airmega 250'],
  'coway-airmega-400': ['Airmega 400'],
  'coway-airmega-prox': ['Airmega ProX'],
  'winix-5510': ['5510'],
};

/** True only when the filter's compatibility list contains an exact (normalized) identifier for this model. */
export function filterFitsModel(compat: string[], model: Pick<ModelVariant, 'id' | 'name' | 'sku'>): boolean {
  const ids = new Set([model.name, model.sku, ...(MODEL_ALIASES[model.id] ?? [])].map(normId).filter(Boolean));
  return compat.some((c) => ids.has(normId(c)));
}

export const combinedDb = (db: number, units: number) => db + 10 * Math.log10(units);

export function annualElectricity(watts: number, units: number, c: CostAssumptions) {
  return (watts / 1000) * c.hoursPerDay * 365 * c.tariffUsdPerKwh * units;
}

export function computeCosts(model: ModelVariant, speedPowerW: number | null, units: number, c: CostAssumptions): Costs {
  const missing: string[] = [];
  const offer = getOffer(model.id);
  const purchase = offer ? offer.priceUsd * units : null;
  if (purchase == null) missing.push('purchase price');

  let annualElec: number | null = null;
  let basis: Costs['electricityBasis'] = null;
  if (speedPowerW != null) { annualElec = annualElectricity(speedPowerW, units, c); basis = 'measured-at-speed'; }
  else if (model.ratedPowerW != null) { annualElec = annualElectricity(model.ratedPowerW, units, c); basis = 'rated-max-power-upper-estimate'; }
  else missing.push('power draw');

  const fs = getFilterSet(model.id);
  let maint: number | null = null, boxes: number | null = null, spend: number | null = null;
  if (fs && fs.priceUsd != null && fs.cycleMonths != null) {
    const cycle = fs.cycleMonths * c.filterIntervalFactor;
    maint = (fs.priceUsd * fs.boxesPerUnit * 12 * units) / cycle;
    if (fs.initialIncludedMonths != null) {
      const firstDue = fs.initialIncludedMonths * c.filterIntervalFactor;
      const perUnit = firstDue >= 36 ? 0 : Math.ceil((36 - firstDue) / cycle - 1e-9);
      boxes = perUnit * fs.boxesPerUnit * units;
      spend = boxes * fs.priceUsd;
    } else missing.push('how long the included filters last');
  } else missing.push('filter price or interval');

  const threeYear = purchase != null && annualElec != null && spend != null ? purchase + 3 * annualElec + spend : null;
  return { purchase, annualElectricity: annualElec, electricityBasis: basis, annualFilterMaintenance: maint, filterBoxes36: boxes, filterSpend36: spend, threeYear, missing };
}

export function matchRoom(room: Room, prefs: Preferences): MatchResult {
  const notes: string[] = [];
  const target = chooseTarget(prefs.concerns, prefs.customEach);
  const metrics = requiredMetrics(prefs.concerns);
  const result: MatchResult = { target, required: null, requiredMetrics: metrics, qualifying: [], unknown: [], excluded: [], notes };

  if (!target) return result; // gas-only: no particle sizing at all
  const required = requiredCfm(room, target.each);
  result.required = required;

  if (prefs.country !== 'US') {
    notes.push('We only hold US buying data so far. The sizing target applies anywhere; check local models, voltage and filter supply.');
    return result;
  }
  const generalOnly = prefs.concerns.filter((c) => c !== 'gases').every((c) => c === 'general');
  const maxUnits = prefs.allowTwoUnits ? 2 : 1;

  for (const model of MODELS) {
    const decision = getDecision(model.id);
    if (!decision || decision.state !== 'eligible') continue;
    if (prefs.diyOnly !== (model.kind === 'diy')) continue;

    const hard: string[] = [];
    const unknowns: string[] = [];

    // Hard gates: active model, regional offer in stock, compatible filter available
    if (model.status !== 'active' || !model.statusSourceId) hard.push(`Model status is ${model.status}.`);
    const offer = getOffer(model.id);
    if (!offer) unknowns.push('No US offer on record.');
    else if (offer.stock === 'out-of-stock') hard.push(`Out of stock at ${offer.seller} when checked.`);
    else if (offer.stock === 'unknown') unknowns.push(`Stock at ${offer.seller} not confirmed.`);
    const fs = getFilterSet(model.id);
    if (!fs || fs.compatibleWith.length === 0) unknowns.push('No confirmed compatible replacement filter.');
    else if (fs.availability === 'out-of-stock') hard.push('Replacement filter out of stock when checked.');
    else if (fs.availability === 'unknown') unknowns.push('Replacement filter availability not confirmed.');

    if (prefs.ionizer === 'none' && model.ionizer !== 'not-fitted') hard.push('Has an ionizer stage and you asked for none.');

    const fsCompat = fs ? filterFitsModel(fs.compatibleWith, model) : false;
    if (fs && fs.compatibleWith.length > 0 && !fsCompat) unknowns.push('Recorded filter does not list this exact model as compatible.');

    // Enumerate every complete configuration: one speed + filter config + ionizer state + source.
    // Metrics are never combined across configurations.
    const tests = getTests(model.id);
    const groups = new Map<string, PerformanceTest[]>();
    for (const t of tests) {
      const k = [t.speed, t.filterConfig, t.ionizerState, t.sourceId].join('|');
      groups.set(k, [...(groups.get(k) ?? []), t]);
    }
    const configs: { tests: PerformanceTest[]; perUnit: number; noise: number | null; usedUnspecified: boolean }[] = [];
    for (const atCfg of groups.values()) {
      let picked: PerformanceTest[] = [];
      let usedUnspecified = false;
      const direct = metrics.map((m) => atCfg.find((t) => t.metric === m));
      if (direct.every(Boolean)) picked = direct as PerformanceTest[];
      else if (generalOnly && !atCfg.some((t) => ['smoke', 'dust', 'pollen'].includes(t.metric))) {
        const u = atCfg.find((t) => t.metric === 'unspecified');
        if (u) { picked = [u]; usedUnspecified = true; }
      }
      if (!picked.length) continue;
      const noises = picked.map((t) => t.noiseDbA);
      const noise = noises.every((n) => n != null) ? Math.max(...(noises as number[])) : null;
      configs.push({ tests: picked, perUnit: Math.min(...picked.map((t) => t.cfm)), noise, usedUnspecified });
    }

    if (!configs.length) {
      if (tests.some((t) => t.metric === 'unspecified') && !generalOnly)
        unknowns.push('Only a CADR with no stated particle type is published, which cannot confirm ' + metrics.join('/') + ' performance.');
      else hard.push(`No ${metrics.join(' + ')} CADR on record.`);
      (hard.length ? result.excluded : result.unknown).push({ model, reasons: [...hard, ...unknowns] });
      continue;
    }
    if (hard.length) { result.excluded.push({ model, reasons: hard }); continue; }

    // Try every configuration × unit count against ALL constraints.
    const feasible: Setup[] = [];
    const why = new Set<string>();
    const unknownWhy = new Set<string>();
    for (const cfg of configs) {
      const cfgUnknown: string[] = [];
      if (prefs.ionizer === 'off-or-none' && model.ionizer !== 'not-fitted') {
        if (model.ionizer !== 'fitted-switchable') cfgUnknown.push('Not documented that the ionizer can be switched off.');
        if (!cfg.tests.every((t) => t.ionizerState === 'off')) cfgUnknown.push('Output with the ionizer off is not documented.');
      }
      for (let n = 1; n <= maxUnits; n++) {
        if (cfg.perUnit * n < required - 1e-9) { why.add(`${cfg.perUnit} CFM${n > 1 ? ` × ${n}` : ''} (${cfg.tests[0].speed}) is below the ${Math.ceil(required)} CFM target.`); continue; }
        const u = [...cfgUnknown];
        if (prefs.maxNoiseDbA != null) {
          if (cfg.noise == null) { u.push(`No output figure measured with sound at the same speed. A published noise range does not show what it delivers at ${prefs.maxNoiseDbA} dBA.`); }
          else if (combinedDb(cfg.noise, n) > prefs.maxNoiseDbA + 1e-9) { why.add(`At ${cfg.tests[0].speed}, ${n} unit${n > 1 ? 's' : ''} ≈ ${combinedDb(cfg.noise, n).toFixed(0)} dBA, over your ${prefs.maxNoiseDbA} dBA limit.`); continue; }
        }
        const costs = computeCosts(model, cfg.tests[0].powerW, n, prefs.costs);
        if (prefs.maxPurchaseUsd != null) {
          if (costs.purchase == null) u.push('Purchase price unknown, so your budget cannot be checked.');
          else if (costs.purchase > prefs.maxPurchaseUsd) { why.add(`Purchase cost $${costs.purchase.toFixed(0)} for ${n} unit${n > 1 ? 's' : ''} is over your $${prefs.maxPurchaseUsd} budget.`); continue; }
        }
        if (prefs.maxAnnualUsd != null) {
          const annual = costs.annualElectricity != null && costs.annualFilterMaintenance != null ? costs.annualElectricity + costs.annualFilterMaintenance : null;
          if (annual == null) u.push('Yearly running cost incomplete, so your yearly budget cannot be checked.');
          else if (annual > prefs.maxAnnualUsd) { why.add(`Yearly running cost ~$${annual.toFixed(0)} for ${n} unit${n > 1 ? 's' : ''} is over your $${prefs.maxAnnualUsd} limit.`); continue; }
        }
        if (u.length || unknowns.length) { [...unknowns, ...u].forEach((r) => unknownWhy.add(r)); continue; }
        const caveats: string[] = [];
        if (cfg.usedUnspecified) caveats.push('Uses a manufacturer CADR with no stated particle type. It may not reflect smoke-sized particles.');
        if (n > 1) caveats.push('Two units counted together assumes both are in the same open space with air mixing between them.');
        if (costs.electricityBasis === 'rated-max-power-upper-estimate') caveats.push('Electricity uses rated maximum power — an upper estimate, not a measurement at this speed.');
        if (cfg.noise == null) caveats.push('Sound at this speed is not on record.');
        if (!cfg.tests[0].isMaxSpeed) caveats.push(`Uses the figure measured at "${cfg.tests[0].speed}", not maximum speed.`);
        feasible.push({
          model, units: n, tests: cfg.tests, metricsUsed: cfg.tests.map((t) => t.metric), perUnitCfm: cfg.perUnit,
          totalCfm: cfg.perUnit * n, achievedEach: achievedEach(cfg.perUnit * n, room),
          combinedNoiseDbA: cfg.noise == null ? null : combinedDb(cfg.noise, n), costs, caveats,
        });
      }
    }
    if (feasible.length) {
      sortSetups(feasible, prefs.sort);
      result.qualifying.push(feasible[0]); // one best feasible setup per model
    } else if (unknownWhy.size) result.unknown.push({ model, reasons: [...unknownWhy] });
    else result.excluded.push({ model, reasons: [...why] });
  }

  sortSetups(result.qualifying, prefs.sort);
  if (result.qualifying.length === 0)
    notes.push('No setup in our checked catalogue meets every requirement. That is a real answer, not an error — see "Not confirmed" and "Ruled out".');
  return result;
}

/** Sort globally by the chosen key; unknown values last; unit count and output only break ties. */
export function sortSetups(list: Setup[], key: SortKey) {
  const val = (s: Setup): number | null =>
    key === 'three-year' ? s.costs.threeYear : key === 'purchase' ? s.costs.purchase : key === 'noise' ? s.combinedNoiseDbA : -s.totalCfm;
  list.sort((a, b) => {
    const va = val(a), vb = val(b);
    if (va != null && vb != null && va !== vb) return va - vb;
    if (va == null && vb != null) return 1;
    if (vb == null && va != null) return -1;
    if (a.units !== b.units) return a.units - b.units;
    return b.totalCfm - a.totalCfm;
  });
}

// ---------- gas guidance ----------
export const GAS_SOURCE_LABELS: Record<GasSource, string> = {
  cooking: 'Cooking smells or fumes',
  renovation: 'New furniture, paint, flooring or renovation',
  'smoke-odour': 'Lingering tobacco or wildfire smoke smell',
  pets: 'Pet or litter-box odour',
  musty: 'Musty / damp smell',
  unknown: 'Not sure where it comes from',
};

export function gasGuidance(source: GasSource | null, persistence: GasPersistence | null): string[] {
  const g: string[] = [];
  switch (source) {
    case 'cooking': g.push('Use a range hood that vents outdoors, switched on while cooking and for a while after. Gas stoves also produce nitrogen dioxide; venting outdoors addresses that better than a purifier.'); break;
    case 'renovation': g.push('Ventilate when outdoor air is suitable (not during wildfire smoke or high outdoor pollution), and ask suppliers for low-emission (e.g. certified low-VOC) products. Emissions usually fall over time; a purifier\'s carbon stage may reduce some of them but is not a substitute.'); break;
    case 'smoke-odour': g.push('Particles and smell are separate problems: size particle filtration with smoke CADR (select wildfire smoke above). Lingering smell is often absorbed in fabrics and surfaces; cleaning them matters more than a filter.'); break;
    case 'pets': g.push('Clean the source often (litter, bedding). Airborne dander is a particle problem — select pet dander above for sizing.'); break;
    case 'musty': g.push('A musty smell usually means moisture. Find and fix the damp (leak, condensation, humidity above ~50–60%). A purifier does not fix the source of mould.'); break;
    case 'unknown': g.push('If you cannot find the source, or smell gas, sewage or burning, treat it as a building problem: call the utility or a qualified professional. Fit a carbon monoxide alarm — a purifier does not remove CO.'); break;
  }
  if (persistence === 'ongoing') g.push('For an ongoing odour, a device with a substantial amount of sorbent media (carbon, often kilograms rather than grams) is more relevant than a thin carbon sheet — but ask for chemical-specific test results, and budget for frequent media replacement.');
  if (persistence === 'occasional') g.push('For occasional odours, ventilating during and after the activity — when outdoor air is suitable — is often the simplest step.');
  return g;
}
